import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import CustomTextField from "Shared/Components/Inputs/CustomTextField";
import type { SxProps, Theme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	type Control,
	Controller,
	type FieldError,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	get,
} from "react-hook-form";
import { FormInputContainer } from "../Container/FormInputContainer";
import { useFieldError } from "../Helpers/getFormErrors";

const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];

type AddressInputProps<FormType extends FieldValues> = {
	id: string;
	name: FieldPath<FormType>;
	control: Control<FormType, object>;
	errors: FieldErrors<FormType>;
	label?: string;
	fullWidth?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	placeholder?: string;
	sxLabel?: SxProps<Theme>;
	countryRestriction?: string;
};

export const AddressInput = <FormType extends FieldValues>({
	id,
	label,
	name,
	control,
	errors,
	fullWidth = false,
	disabled,
	readOnly,
	placeholder,
	sxLabel,
	countryRestriction,
}: AddressInputProps<FormType>) => {
	const { hasError, errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "",
		libraries: GOOGLE_MAPS_LIBRARIES,
	});

	const [suggestions, setSuggestions] = useState<string[]>([]);
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchSuggestions = useCallback(
		(input: string) => {
			if (!isLoaded || !input) {
				setSuggestions([]);
				return;
			}
			const service = new window.google.maps.places.AutocompleteService();
			service.getPlacePredictions(
				{
					input,
					...(countryRestriction && {
						componentRestrictions: { country: countryRestriction },
					}),
				},
				(predictions, status) => {
					if (
						status === window.google.maps.places.PlacesServiceStatus.OK &&
						predictions
					) {
						setSuggestions(predictions.map((p) => p.description));
					} else {
						setSuggestions([]);
					}
				},
			);
		},
		[isLoaded, countryRestriction],
	);

	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, []);

	return (
		<>
			{label && (
				<CustomFormLabel sx={sxLabel} htmlFor={id}>
					{label}
				</CustomFormLabel>
			)}
			<Controller
				name={name}
				control={control}
				render={({ field: { onChange, value } }) => (
					<FormInputContainer>
						<Autocomplete
							freeSolo
							filterOptions={(x) => x}
							options={suggestions}
							inputValue={value ?? ""}
							disabled={disabled || !isLoaded}
							onInputChange={(_, newVal, reason) => {
								if (reason === "reset") return;
								onChange(newVal);
								if (debounceTimer.current) {
									clearTimeout(debounceTimer.current);
								}
								debounceTimer.current = setTimeout(() => {
									fetchSuggestions(newVal);
								}, 300);
							}}
							onChange={(_, newVal) => {
								if (typeof newVal === "string") {
									onChange(newVal);
									setSuggestions([]);
								}
							}}
							renderInput={(params) => (
								<CustomTextField
									{...params}
									id={id}
									variant="outlined"
									fullWidth={fullWidth}
									placeholder={placeholder}
									error={!!hasError}
									helperText={hasError ? errorMessage : ""}
									inputProps={{
										...params.inputProps,
										readOnly,
										autoComplete: "off",
									}}
								/>
							)}
						/>
					</FormInputContainer>
				)}
			/>
		</>
	);
};
